import React, { useEffect } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import {
  FaAngular,
  FaServer,
  FaDatabase,
  FaCloud,
  FaCode,
  FaCogs,
  FaRocket,
  FaMobileAlt,
} from "react-icons/fa";
import { Link } from "react-router-dom";

// Angular Topics
const topics = [
  {
    icon: <FaAngular />,
    title: "Angular Basics",
    description: "Understand Components, Modules, and Angular CLI.",
  },
  {
    icon: <FaCode />,
    title: "Templates & Data Binding",
    description: "Learn property, event, and two-way binding in Angular.",
  },
  {
    icon: <FaCogs />,
    title: "Services & DI",
    description: "Use Angular services and dependency injection effectively.",
  },
  {
    icon: <FaDatabase />,
    title: "HTTP & APIs",
    description: "Fetch and post data using HttpClientModule.",
  },
  {
    icon: <FaRocket />,
    title: "Routing & Navigation",
    description: "Create dynamic single-page apps with Angular Router.",
  },
  {
    icon: <FaMobileAlt />,
    title: "Angular & Mobile",
    description: "Build responsive mobile-friendly Angular apps.",
  },
];

// Usage Areas
const usageAreas = [
  {
    icon: <FaServer />,
    title: "Web Applications",
    description: "Build scalable SPAs with Angular.",
  },
  {
    icon: <FaCloud />,
    title: "Cloud & PWA",
    description: "Deploy Angular apps as Progressive Web Apps.",
  },
  {
    icon: <FaDatabase />,
    title: "Enterprise Apps",
    description: "Manage complex enterprise systems with Angular.",
  },
  {
    icon: <FaMobileAlt />,
    title: "Mobile Frontend",
    description: "Use Angular for mobile app frontends.",
  },
  {
    icon: <FaRocket />,
    title: "Real-time Apps",
    description: "Integrate WebSockets for live data apps.",
  },
  {
    icon: <FaCogs />,
    title: "Automation & Tools",
    description: "Leverage Angular CLI and DevOps pipelines.",
  },
];

// Future Scope
const futureScope = [
  {
    icon: "🚀",
    title: "Modern SPAs",
    text: "Angular powers large-scale single-page applications.",
  },
  {
    icon: "🌐",
    title: "Fullstack Integration",
    text: "Combine Angular with Node, Firebase, or .NET backends.",
  },
  {
    icon: "📱",
    title: "Mobile & PWA",
    text: "Build progressive web apps and mobile frontends.",
  },
  {
    icon: "🔒",
    title: "Enterprise Adoption",
    text: "Angular is widely adopted in enterprise solutions.",
  },
];

const AngularAnimation = () => {
  useEffect(() => {
    AOS.init({ duration: 1000, once: true });
  }, []);

  return (
    <div
      style={{
        background: "#f9f9f9",
        color: "#222",
      }}
      className="overflow-hidden"
    >
      <header
        style={{
          position: "relative",
          padding: "80px 20px",
          textAlign: "center",
          color: "#fff",
          backgroundImage:
            "url('/assets/ang.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        {/* Dark Overlay for readability */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: "rgba(221, 3, 48, 0.85)", // Angular red overlay
            zIndex: 1,
          }}
        ></div>

        <div style={{ position: "relative", zIndex: 2 }}>
          <h1
            className="text-4xl md:text-6xl font-extrabold mb-4"
            data-aos="fade-down"
          >
            Angular <span style={{ color: "#61dafb" }}>Development</span>
          </h1>
          <p
            data-aos="fade-up"
            style={{ fontSize: "1.2rem", marginTop: "10px" }}
          >
            Learn frontend frameworks, SPA architecture, and career growth
          </p>
          <Link
            to="/contact"
            data-aos="zoom-in-up"
            className="px-8 py-3 mt-6 inline-block bg-white text-red-600 rounded-full font-bold shadow-md hover:bg-gray-200 transition"
            style={{
              marginTop: "20px",
              display: "inline-block",
              textDecoration: "none",
            }}
          >
            Enroll Now
          </Link>
        </div>
      </header>

      {/* WHAT WE SHOULD LEARN */}
      <section
        style={{ padding: "60px 20px", maxWidth: "1200px", margin: "0 auto" }}
      >
        <h2
          style={{
            textAlign: "center",
            marginBottom: "40px",
            fontSize: "2.5rem",
            fontWeight: "bold",
          }}
          data-aos="fade-up"
        >
          What We Should Learn
        </h2>
        <div
          style={{
            display: "flex",
            gap: "30px",
            flexWrap: "wrap",
            justifyContent: "center",
          }}
        >
          <div
            style={{ flex: "1 1 300px", minWidth: "280px", maxWidth: "600px" }}
          >
            {topics.map((topic, i) => (
              <div
                key={i}
                className="usage-card"
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "20px",
                  background: "#fff3f5",
                  padding: "15px",
                  borderRadius: "10px",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  color: "#333",
                }}
                data-aos="fade-up"
                data-aos-delay={i * 100}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-5px)";
                  e.currentTarget.style.boxShadow =
                    "0 8px 20px rgba(221, 3, 48, 0.4)";
                  e.currentTarget.style.background = "#fff"; // Card turns white
                  const title = e.currentTarget.querySelector("h4");
                  if (title) title.style.color = "#dd0330"; // Title turns red
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "none";
                  e.currentTarget.style.background = "#fff3f5"; // Reset background
                  const title = e.currentTarget.querySelector("h4");
                  if (title) title.style.color = "#333"; // Reset title color
                }}
              >
                <div
                  style={{
                    fontSize: "2rem",
                    marginRight: "15px",
                    color: "#dd0330",
                    flexShrink: 0,
                  }}
                >
                  {topic.icon}
                </div>
                <div>
                  <h4 style={{ margin: 0, fontWeight: "bold", color: "#333" }}>
                    {topic.title}
                  </h4>
                  <p style={{ fontSize: "0.95rem", color: "#555" }}>
                    {topic.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
          <div
            style={{
              flex: "1 1 300px",
              minWidth: "280px",
              maxWidth: "600px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
            data-aos="fade-left"
          >
            <img
              src="/assets/886fda84cd2014e7e59a1a5521fe1414.jpg"
              alt="Angular"
              style={{
                width: "100%",
                height: "auto",
                maxWidth: "450px",
                borderRadius: "15px",
                boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
              }}
            />
          </div>
        </div>
      </section>

      {/* WHERE IT CAN BE USED */}
      <section className="py-5" style={{ background: "#ffe5e8" }}>
        <h1 className="text-center mb-5 fw-bold" data-aos="fade-up">
          Where Angular Can Be Used
        </h1>
        <div className="container">
          <div className="row g-4">
            {usageAreas.map((area, i) => {
              const colors = [
                "#dd0330",
                "#ff4081",
                "#f44336",
                "#e91e63",
                "#c2185b",
                "#ad1457",
              ];
              return (
                <div
                  key={i}
                  className="col-sm-6 col-lg-4"
                  data-aos="zoom-in-up"
                  data-aos-delay={i * 100}
                >
                  <div
                    className="p-4 text-center rounded position-relative h-100 usage-card"
                    style={{
                      background: "#fff",
                      boxShadow: "0 4px 15px rgba(0,0,0,0.05)",
                    }}
                  >
                    <div
                      style={{
                        position: "absolute",
                        top: 0,
                        right: 0,
                        width: "45px",
                        height: "45px",
                        background: `linear-gradient(135deg, ${
                          colors[i % colors.length]
                        }, transparent)`,
                        borderTopRightRadius: "15px",
                        boxShadow: `0 0 10px ${colors[i % colors.length]}`,
                      }}
                    ></div>
                    <div
                      style={{
                        fontSize: "2rem",
                        color: colors[i % colors.length],
                      }}
                    >
                      {area.icon}
                    </div>
                    {/* <h5 className="fw-bold">{area.title}</h5> */}
                    <h5 className="fw-bold text-purple-800 hover:text-yellow-500 hover:scale-105 transition-transform duration-300">
                      {area.title}
                    </h5>

                    <p className="text-muted small">{area.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FUTURE SCOPE */}
      <section
        style={{
          padding: "60px 20px",
          background: "#fff",
          color: "#333",
          textAlign: "center",
          overflow: "hidden",
        }}
      >
        <h2
          style={{
            fontSize: "2.5rem",
            fontWeight: "bold",
            marginBottom: "30px",
          }}
          data-aos="fade-up"
        >
          Future Scope
        </h2>

        <div
          id="my-sc"
          style={{ display: "flex", width: "100%", overflow: "hidden" }}
        >
          {[0, 1].map((groupIndex) => (
            <div
              key={groupIndex}
              className="scroll-group"
              style={{ display: "flex", gap: "20px", flexShrink: 0 }}
            >
              {futureScope.map((item, i) => {
                const colors = ["#ff4081", "#dd0330", "#f06292", "#e91e63"];
                return (
                  <div
                    key={i}
                    className="usage-card"
                    style={{
                      position: "relative",
                      background: "linear-gradient(145deg, #fff3f5, #ffe5e8)",
                      borderRadius: "20px",
                      padding: "25px",
                      boxShadow: "0 8px 20px rgba(0,0,0,0.08)",
                      border: "1px solid #eee",
                      transition: "transform 0.3s ease",
                      minWidth: "250px",
                      flexShrink: 0,
                      animation: "shake 3s infinite ease-in-out",
                      animationDelay: `${i * 500}ms`,
                      color: "#333",
                    }}
                    data-aos="fade-up"
                    data-aos-delay={i * 150}
                  >
                    <div
                      style={{
                        position: "absolute",
                        top: 0,
                        right: 0,
                        width: "35px",
                        height: "35px",
                        background: `linear-gradient(135deg, ${
                          colors[i % colors.length]
                        }, transparent)`,
                        borderTopRightRadius: "20px",
                        boxShadow: `0 0 10px ${colors[i % colors.length]}`,
                      }}
                    ></div>
                    <div style={{ fontSize: "2rem", marginBottom: "10px" }}>
                      {item.icon}
                    </div>
                    <h3
                      style={{
                        fontSize: "1.3rem",
                        fontWeight: "bold",
                        marginBottom: "10px",
                      }}
                    >
                      {item.title}
                    </h3>
                    <p style={{ fontSize: "0.95rem", color: "#555" }}>
                      {item.text}
                    </p>
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        <style jsx>{`
          #my-sc {
            overflow: hidden;
            white-space: nowrap;
          }
          .scroll-group {
            animation: marquee 20s linear infinite;
          }
          @keyframes marquee {
            0% {
              transform: translateX(0);
            }
            100% {
              transform: translateX(-50%);
            }
          }
          @keyframes shake {
            0%,
            100% {
              transform: translate(0, 0) rotate(0);
            }
            20% {
              transform: translate(-1px, 1px) rotate(-0.5deg);
            }
            40% {
              transform: translate(1px, -1px) rotate(0.5deg);
            }
            60% {
              transform: translate(-1px, 1px) rotate(-0.5deg);
            }
            80% {
              transform: translate(1px, -1px) rotate(0.5deg);
            }
          }
        `}</style>
      </section>

      {/* ✅ FOOTER */}
      <footer
        style={{
          background: "#111",
          color: "#fff",
          padding: "40px 20px",
          textAlign: "center",
        }}
        data-aos="fade-up"
      >
        <h3 style={{ marginBottom: "15px" }}>
          Ready to boost your Angular skills?
        </h3>
        <p style={{ marginBottom: "20px", color: "#bbb" }}>
          Join our course and become a frontend pro with Angular
        </p>
        <Link
          to="/contact"
          className="px-8 py-3 bg-emerald-500 text-white rounded-full font-bold shadow-md hover:bg-emerald-600 transition animate-pulse"
        >
          Enroll Now
        </Link>
      </footer>
    </div>
  );
};

export default AngularAnimation;
